import { useState, useEffect, useRef, useCallback } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '@/utils/userFeedback';

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: UserFeedback['type'];
  initialCategory?: string;
  initialError?: string;
}

const FEEDBACK_TYPES: { value: UserFeedback['type']; label: string }[] = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'usability_issue', label: 'Usability Issue' },
  { value: 'performance_issue', label: 'Performance Issue' },
];

const CATEGORIES = [
  'general',
  'dosing',
  'calculations',
  'ui_ux',
  'performance',
  'bug',
  'feature_request',
  'accessibility',
  'safety',
];

const PRIORITIES: { value: UserFeedback['priority']; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function formatCategory(c: string): string {
  return c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function FeedbackForm({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}: FeedbackFormProps): JSX.Element | null {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as UserFeedback['priority'],
  });

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => contentRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      try {
        const payload = { ...formData, url: window.location.href };
        const id = userFeedbackManager.submitFeedback(payload);
        setFeedbackId(id);

        const mailtoUrl = buildMailtoUrl(payload);
        if (mailtoUrl) window.location.href = mailtoUrl;

        setSubmitted(true);
      } catch (err) {
        console.error('Feedback submission failed:', err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData],
  );

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Send Feedback">
      <div
        ref={contentRef}
        className="modal feedback-modal"
        role="document"
        tabIndex={-1}
      >
        <div className="feedback-header">
          <h2 className="modal-title">Send Feedback</h2>
          <button
            className="feedback-close-btn"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={24} strokeWidth={2} className="feedback-icon" />
          </button>
        </div>

        {submitted ? (
          <div className="feedback-success">
            <p className="feedback-success-title">Thank you!</p>
            <p className="feedback-success-text">
              Your mail client should have opened. Send the pre-filled email to complete.
            </p>
            {feedbackId && <p className="feedback-ref">Ref: {feedbackId}</p>}
            <button className="btn" onClick={onClose} type="button">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            <div className="field">
              <label htmlFor="fb-type">Type</label>
              <select
                id="fb-type"
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                {FEEDBACK_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>
                    {ft.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="fb-category">Category</label>
              <select
                id="fb-category"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {formatCategory(c)}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <span className="field-label-text">Priority</span>
              <div className="feedback-priority-row">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => handleChange('priority', p.value)}
                    className={`feedback-priority-btn${
                      formData.priority === p.value ? ' feedback-priority-active' : ''
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label htmlFor="fb-title">Title *</label>
              <input
                id="fb-title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Summarize the issue in a few words"
              />
            </div>

            <div className="field">
              <label htmlFor="fb-description">Description *</label>
              <textarea
                id="fb-description"
                rows={5}
                required
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="What happened? What did you expect instead?"
                className="modal-textarea"
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn"
                disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
              >
                {isSubmitting ? 'Opening mail...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

interface FeedbackButtonProps {
  onOpen: (type?: UserFeedback['type'], category?: string, error?: string) => void;
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onOpen()}
      className="feedback-fab"
      aria-label="Send feedback"
      title="Send feedback"
    >
      <MessageSquare size={24} strokeWidth={2} className="feedback-fab-icon" />
    </button>
  );
}
